import Fuse from "fuse.js";

import medData from "./med-lookup-data.json";

export interface MedLookupEntry {
    name: string;
    route: string;
    fullName: string;
    totalCount: number;
    searchTerms: string[];
    dosages: { value: string; pct: string }[];
    frequencies: { value: string; pct: string }[];
    adminNotes: { value: string; pct: string }[];
    diagnoses: { code: string; description: string; pct: string }[];
}

const entries = medData as MedLookupEntry[];

// Build a flat list of (lowercased search term → entry) for prefix matching
const prefixIndex: { term: string; entry: MedLookupEntry }[] = [];
for (const entry of entries) {
    for (const t of entry.searchTerms) {
        prefixIndex.push({ term: t.toLowerCase(), entry });
    }
    // Also add the display name
    prefixIndex.push({ term: entry.name.toLowerCase(), entry });
}

// Fuse.js only for fuzzy fallback (typo correction on exact name lookups)
const fuse = new Fuse(entries, {
    keys: ["name", "searchTerms"],
    threshold: 0.2,
    includeScore: true,
    minMatchCharLength: 3
});

/**
 * Search the internal med lookup table using prefix matching.
 * "ato" matches "Atorvastatin" but NOT "Melatonin".
 */
export function searchMedLookup(query: string): MedLookupEntry[] {
    if (!query || query.length < 2) {
        return [];
    }
    const lower = query.toLowerCase();

    // Prefix match: find entries where any search term STARTS with the query
    const seen = new Set<string>();
    const results: MedLookupEntry[] = [];
    for (const { term, entry } of prefixIndex) {
        if (term.startsWith(lower) && !seen.has(entry.fullName)) {
            seen.add(entry.fullName);
            results.push(entry);
        }
    }

    // Sort by total count (most common first)
    results.sort((a, b) => b.totalCount - a.totalCount);
    return results.slice(0, 10);
}

/**
 * Find an exact match by medication name (for photo extraction matching).
 * Checks all variant names, case-insensitive. Falls back to fuzzy.
 */
export function findMedByName(name: string): MedLookupEntry | null {
    const lower = name.toLowerCase().trim();
    for (const entry of entries) {
        for (const term of entry.searchTerms) {
            if (term.toLowerCase() === lower) {
                return entry;
            }
        }
    }
    // Fuzzy fallback for typos
    const results = fuse.search(name, { limit: 1 });
    if (results.length > 0 && results[0].score !== undefined && results[0].score < 0.2) {
        return results[0].item;
    }
    return null;
}

/**
 * Total number of meds in the lookup table.
 */
export const MED_LOOKUP_COUNT = entries.length;
